// Icon 컴포넌트
import { IconName, ICONS } from './constants';
import { FC, SVGProps } from 'react';

// import icons
// common
import ProfileIcon from '@/assets/icons/profile.svg?react';
import StatusIcon from '@/assets/icons/status.svg?react';
import PriorityIcon from '@/assets/icons/priority.svg?react';
import LeaderIcon from '@/assets/icons/leader.svg?react';
import ContributorIcon from '@/assets/icons/contributor.svg?react';
import ProjectIcon from '@/assets/icons/project.svg?react';
import DueDateIcon from '@/assets/icons/due_date.svg?react';
import CalendarIcon from '@/assets/icons/calendar.svg?react';
import PersonalReportIcon from '@/assets/icons/personal_report.svg?react';
import TeamReportIcon from '@/assets/icons/team_report.svg?react';

// arrow
import ExpandIcon from '@/assets/icons/arrow_icons/expand.svg?react';
import CollapseIcon from '@/assets/icons/arrow_icons/collapse.svg?react';
import FeedbackArrowIcon from '@/assets/icons/arrow_icons/feedback.svg?react';

// goal
import BacklogIcon from '@/assets/icons/goal_icons/backlog.svg?react';
import PlannedIcon from '@/assets/icons/goal_icons/planned.svg?react';
import InProgressIcon from '@/assets/icons/goal_icons/in_progress.svg?react';
import AtRiskIcon from '@/assets/icons/goal_icons/at_risk.svg?react';
import CompletedIcon from '@/assets/icons/goal_icons/completed.svg?react';
import PausedIcon from '@/assets/icons/goal_icons/paused.svg?react';
import CanceledIcon from '@/assets/icons/goal_icons/canceled.svg?react';

// priority
import HighestPriorityIcon from '@/assets/icons/priority_icons/highest.svg?react';
import HighPriorityIcon from '@/assets/icons/priority_icons/high.svg?react';
import MediumPriorityIcon from '@/assets/icons/priority_icons/medium.svg?react';
import LowPriorityIcon from '@/assets/icons/priority_icons/low.svg?react';
import LowestPriorityIcon from '@/assets/icons/priority_icons/lowest.svg?react';

// note
import NoteIcon from '@/assets/icons/note_icons/note.svg?react';
import LinkIcon from '@/assets/icons/note_icons/link.svg?react';

// ai
import AIAssistantIcon from '@/assets/icons/ai_icons/assistant.svg?react';
import AITranslateIcon from '@/assets/icons/ai_icons/translate.svg?react';
import AskAIIcon from '@/assets/icons/ai_icons/ask_ai.svg?react';
import AIMailIcon from '@/assets/icons/ai_icons/aimail.svg?react';
import AITaskIcon from '@/assets/icons/ai_icons/aitask.svg?react';
import AILinkIcon from '@/assets/icons/ai_icons/ailink.svg?react';
import AireportIcon from '@/assets/icons/ai_icons/aireport.svg?react';
import LaikaLogoIcon from '@/assets/icons/ai_icons/laika_logo.svg?react';
import LaikaCornerIcon from '@/assets/icons/ai_icons/laika_corner.svg?react';
import LaikaFloatingIcon from '@/assets/icons/ai_icons/laika_floating.svg?react';
import LaikaFootprintIcon from '@/assets/icons/ai_icons/laika_footprint.svg?react';

// indicator
import DollarIcon from '@/assets/icons/indicator_icons/dollar.svg?react';
import NumberIcon from '@/assets/icons/indicator_icons/number.svg?react';
import PercentIcon from '@/assets/icons/indicator_icons/percent.svg?react';
import WonIcon from '@/assets/icons/indicator_icons/won.svg?react';

// todo
import TodoAddIcon from '@/assets/icons/todo_icons/todo_add.svg?react';
import NowIcon from '@/assets/icons/todo_icons/now.svg?react';

// svg 컴포넌트의 타입 정의
type SVGComponent = FC<SVGProps<SVGSVGElement>>;

// IconComponents를 Record 타입으로 정의
const IconComponents: Record<IconName, SVGComponent> = {
    // common
    [ICONS.COMMON.PROFILE]: ProfileIcon,
    [ICONS.COMMON.STATUS]: StatusIcon,
    [ICONS.COMMON.PRIORITY]: PriorityIcon,
    [ICONS.COMMON.LEADER]: LeaderIcon,
    [ICONS.COMMON.CONTRIBUTOR]: ContributorIcon,
    [ICONS.COMMON.PROJECT]: ProjectIcon,
    [ICONS.COMMON.DUE_DATE]: DueDateIcon,
    [ICONS.COMMON.CALENDAR]: CalendarIcon,
    [ICONS.COMMON.PERSONAL_REPORT]: PersonalReportIcon,
    [ICONS.COMMON.TEAM_REPORT]: TeamReportIcon,

    // arrow
    [ICONS.ARROW.EXPAND]: ExpandIcon,
    [ICONS.ARROW.COLLAPSE]: CollapseIcon,
    [ICONS.ARROW.FEEDBACK_ARROW]: FeedbackArrowIcon,

    // goal
    [ICONS.GOAL.BACKLOG]: BacklogIcon,
    [ICONS.GOAL.PLANNED]: PlannedIcon,
    [ICONS.GOAL.IN_PROGRESS]: InProgressIcon,
    [ICONS.GOAL.AT_RISK]: AtRiskIcon,
    [ICONS.GOAL.COMPLETED]: CompletedIcon,
    [ICONS.GOAL.PAUSED]: PausedIcon,
    [ICONS.GOAL.CANCELED]: CanceledIcon,

    // priority
    [ICONS.PRIORITY.HIGHEST]: HighestPriorityIcon,
    [ICONS.PRIORITY.HIGH]: HighPriorityIcon,
    [ICONS.PRIORITY.MEDIUM]: MediumPriorityIcon,
    [ICONS.PRIORITY.LOW]: LowPriorityIcon,
    [ICONS.PRIORITY.LOWEST]: LowestPriorityIcon,

    // indicator
    [ICONS.INDICATOR.DOLLAR]: DollarIcon,
    [ICONS.INDICATOR.NUMBER]: NumberIcon,
    [ICONS.INDICATOR.PERCENT]: PercentIcon,
    [ICONS.INDICATOR.WON]: WonIcon,

    // note
    [ICONS.NOTE.NOTE]: NoteIcon,
    [ICONS.NOTE.LINK]: LinkIcon,

    // ai
    [ICONS.AI.ASSISTANT]: AIAssistantIcon,
    [ICONS.AI.TRANSLATE]: AITranslateIcon,
    [ICONS.AI.ASK_AI]: AskAIIcon,
    [ICONS.AI.AI_MAIL]: AIMailIcon,
    [ICONS.AI.AI_TASK]: AITaskIcon,
    [ICONS.AI.AI_LINK]: AILinkIcon,
    [ICONS.AI.AIREPORT]: AireportIcon,
    [ICONS.AI.LAIKA_LOGO]: LaikaLogoIcon,
    [ICONS.AI.LAIKA_CORNER]: LaikaCornerIcon,
    [ICONS.AI.LAIKA_FLOATING]: LaikaFloatingIcon,
    [ICONS.AI.LAIKA_FOOTPRINT]: LaikaFootprintIcon,

    // todo
    [ICONS.TODO.ADD]: TodoAddIcon,
    [ICONS.TODO.NOW]: NowIcon,
} as const;

interface IconProps {
    name: IconName;
    size?: number;
    color?: string;
    className?: string;
    filled?: boolean;
    outlined?: boolean;
}

function getColorFilter(color: string) {
    if (!color) return '';
    // 하드코딩된 일부 색상값들 (자주 사용되는 색상들)
    // https://codepen.io/sosuke/pen/Pjoqqp 에서 필터값 추출 가능
    const filterMap: Record<string, string> = {
        '#00DBFF':
            'invert(58%) sepia(83%) saturate(1728%) hue-rotate(155deg) brightness(105%) contrast(101%)',
        '#000000': 'brightness(0) saturate(100%)',
        '#FFFFFF':
            'invert(100%) sepia(100%) saturate(0%) hue-rotate(47deg) brightness(104%) contrast(101%)',
        '#CCAF30':
            'invert(69%) sepia(100%) saturate(336%) hue-rotate(2deg) brightness(85%) contrast(92%)',
    };

    // 미리 정의된 필터값이 있으면 사용
    if (filterMap[color]) {
        return filterMap[color];
    }

    return '';
}

export function Icon({
    name,
    size,
    color,
    className,
    filled = false,
    outlined = true,
}: IconProps) {
    // name이 IconComponents에 있는 경우
    if (!(name in IconComponents)) {
        console.warn(`Icon "${name}" not found in IconComponents`);
        return null;
    }

    const IconComponent = IconComponents[name];
    const wrapperStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(size && { width: size, height: size }),
        background: 'none', // 배경 제거
        ...(color && {
            filter: getColorFilter(color),
        }),
    };

    const svgProps = {
        ...(size && { width: size, height: size }),
        style: {
            '--icon-fill': filled ? 'currentColor' : 'none',
            '--icon-stroke': outlined ? 'currentColor' : 'none',
        } as React.CSSProperties,
    };

    return (
        <div style={wrapperStyle} className={className}>
            <IconComponent {...svgProps} />
        </div>
    );
}

export { ICONS };
